/**
 * MoveToExceptionsDialog — Kanban Board helper.
 *
 * Shown when the user drops a card onto the Exceptions column.
 * Asks for:
 *   1. Sub-status: Blocked vs Escalated (radio)
 *   2. Reason (required textarea)
 *
 * On Confirm → calls onConfirm(subStatus, reason).
 * On Cancel  → calls onCancel() which reverts the optimistic move.
 */

import React, { useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { RadioGroup } from '@/components/ui/Radio';
import { t } from '@/lib/i18n';

export type ExceptionSubStatus = 'blocked' | 'escalated';

interface MoveToExceptionsDialogProps {
  isOpen: boolean;
  itemTitle: string;
  onConfirm: (subStatus: ExceptionSubStatus, reason: string) => void;
  onCancel: () => void;
}

export function MoveToExceptionsDialog({
  isOpen,
  itemTitle,
  onConfirm,
  onCancel,
}: MoveToExceptionsDialogProps) {
  const [subStatus, setSubStatus] = useState<ExceptionSubStatus>('blocked');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    if (!submitting) {
      setSubStatus('blocked');
      setReason('');
      onCancel();
    }
  }

  async function handleConfirm() {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(subStatus, reason.trim());
    } finally {
      setSubmitting(false);
      setSubStatus('blocked');
      setReason('');
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Confirm move to Exceptions')}
      description={
        <span className="text-sm text-muted-foreground">
          {t('Moving')} <span className="font-medium text-foreground">{itemTitle}</span>
        </span>
      }
      size="sm"
      closeOnOverlayClick={!submitting}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason.trim() || submitting}
            isLoading={submitting}
          >
            {t('Confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Sub-status radio */}
        <RadioGroup
          name="exceptionSubStatus"
          label={t('Move to')}
          value={subStatus}
          onChange={(v) => setSubStatus(v as ExceptionSubStatus)}
          options={[
            { value: 'blocked', label: t('Blocked') },
            { value: 'escalated', label: t('Escalated') },
          ]}
        />

        {/* Reason */}
        <div>
          <label
            htmlFor="exception-reason"
            className="block text-sm font-medium text-foreground mb-1"
          >
            {t('Reason')}
            <span className="text-error-500 ml-1" aria-hidden="true">*</span>
          </label>
          <Textarea
            id="exception-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              subStatus === 'blocked'
                ? t('What is blocking this item?')
                : t('Why is this item being escalated?')
            }
            rows={3}
            required
            data-testid="exception-reason-input"
          />
          {reason.trim() === '' && submitting && (
            <p className="text-xs text-error-500 mt-1" role="alert">
              {t('Reason is required')}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
